import { useState, type ReactNode } from 'react';
import { Reorder, useDragControls, type DragControls } from 'framer-motion';
import { useFieldArray, useWatch, type UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, GripVertical, Info, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { CustomFormBuilder } from './CustomFormBuilder';
import { type EventFormValues } from '@/lib/schemas/event';
import {
  saveTicketDefault,
  ticketFromPreset,
  type TicketPreset,
} from '@/lib/event-ticket-defaults';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface TicketFormProps {
  form: UseFormReturn<EventFormValues>;
  communityId: string;
}

type TicketValues = EventFormValues['tickets'][number];

const PRESET_LABELS: Record<TicketPreset, string> = {
  member: 'Member',
  guest: 'Guest / Visitor',
};

function parseNonNegativeInt(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

function ticketTitle(ticket: TicketValues, index: number) {
  if (ticket.name?.trim()) return ticket.name.trim();
  if (ticket.preset) return `${PRESET_LABELS[ticket.preset]} Ticket`;
  return `Ticket #${index + 1}`;
}

function DragHandle({
  controls,
  label,
}: {
  controls: DragControls;
  label: string;
}) {
  return (
    <button
      type="button"
      className="inline-flex h-9 w-8 shrink-0 touch-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
      aria-label={`Drag to reorder ${label}`}
      onPointerDown={(event) => controls.start(event)}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
}

function SortableTicketItem({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: (controls: DragControls) => ReactNode;
}) {
  const controls = useDragControls();

  return (
    <Reorder.Item
      value={id}
      as="div"
      dragListener={false}
      dragControls={controls}
      className={cn('relative list-none', className)}
    >
      {children(controls)}
    </Reorder.Item>
  );
}

function TicketLabel({
  form,
  ticket,
  index,
  fieldId,
  enabled,
  onEnabledChange,
  onEdit,
  onRemove,
}: {
  form: UseFormReturn<EventFormValues>;
  ticket: TicketValues;
  index: number;
  fieldId: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const title = ticketTitle(ticket, index);

  return (
    <SortableTicketItem id={fieldId}>
      {(controls) => (
        <div
          className={cn(
            'space-y-3 rounded-2xl border px-3 py-3 sm:px-4',
            enabled
              ? 'border-border bg-card'
              : 'border-dashed border-amber-500/50 bg-amber-50 shadow-sm dark:border-amber-400/40 dark:bg-amber-950/40'
          )}
        >
          <div className="flex items-center gap-2">
            <DragHandle controls={controls} label={title} />
            <Checkbox
              checked={enabled}
              onCheckedChange={(checked) => onEnabledChange(checked === true)}
              aria-label={enabled ? `Disable ${title}` : `Enable ${title}`}
              className={cn(!enabled && 'border-amber-700/70 bg-white dark:border-amber-200/80')}
            />
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'truncate text-sm font-semibold',
                  enabled ? 'text-foreground' : 'text-amber-950 dark:text-amber-50'
                )}
              >
                {title}
              </p>
              {!enabled ? (
                <p className="text-xs font-medium text-amber-800 dark:text-amber-200">Disabled</p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 px-2.5"
                onClick={onEdit}
              >
                Edit
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 px-2.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={onRemove}
              >
                Delete
              </Button>
            </div>
          </div>

          <FormField
            control={form.control}
            name={`tickets.${index}.price`}
            render={({ field: priceField }) => (
              <FormItem className="space-y-1">
                <FormLabel className="text-xs text-muted-foreground">Price (₹)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    inputMode="numeric"
                    className="h-9 w-full [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    value={priceField.value ?? 0}
                    onChange={(e) => {
                      const next = parseNonNegativeInt(e.target.value);
                      priceField.onChange(next ?? 0);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                        e.preventDefault();
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </SortableTicketItem>
  );
}

export function TicketForm({ form, communityId }: TicketFormProps) {
  const { fields, prepend, remove, replace } = useFieldArray({
    control: form.control,
    name: 'tickets',
  });
  const tickets = useWatch({ control: form.control, name: 'tickets' }) ?? [];
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [defaultSavePreset, setDefaultSavePreset] = useState<{
    index: number;
    preset: TicketPreset;
  } | null>(null);

  const fieldIds = fields.map((field) => field.id);
  const ticketPendingDelete =
    deleteIndex == null ? null : (tickets[deleteIndex] ?? null);

  function isDuplicateName(name: string, excludeIndex?: number) {
    const normalized = name.trim().toLowerCase();
    if (!normalized) return false;
    return form
      .getValues('tickets')
      .some(
        (ticket, i) =>
          i !== excludeIndex && ticket.name?.trim().toLowerCase() === normalized
      );
  }

  function uniqueFallbackName(base: string, excludeIndex?: number) {
    const root = base.trim() || 'Ticket';
    let candidate = root;
    let suffix = 2;
    while (isDuplicateName(candidate, excludeIndex)) {
      candidate = `${root} ${suffix}`;
      suffix += 1;
    }
    return candidate;
  }

  function ensureTicketNamed(index: number) {
    const ticket = form.getValues(`tickets.${index}`);
    const currentName = ticket?.name?.trim();
    const nextName = currentName
      ? isDuplicateName(currentName, index)
        ? uniqueFallbackName(currentName, index)
        : currentName
      : uniqueFallbackName(ticketTitle(ticket, index), index);

    if (nextName !== ticket?.name?.trim()) {
      form.setValue(`tickets.${index}.name`, nextName, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    form.clearErrors(`tickets.${index}.name`);
  }

  function closeEditingTicket() {
    if (editingIndex == null) return;
    ensureTicketNamed(editingIndex);
    setEditingIndex(null);
  }

  function beginEditing(index: number) {
    if (editingIndex != null && editingIndex !== index) {
      ensureTicketNamed(editingIndex);
    }
    setEditingIndex(index);
  }

  function handleReorder(newOrder: string[]) {
    const currentTickets = form.getValues('tickets');
    const byId = new Map(fields.map((field, index) => [field.id, currentTickets[index]]));
    const editingId = editingIndex != null ? fields[editingIndex]?.id : null;
    const deleteId = deleteIndex != null ? fields[deleteIndex]?.id : null;
    const defaultId =
      defaultSavePreset != null ? fields[defaultSavePreset.index]?.id : null;

    replace(newOrder.map((id) => byId.get(id)!).filter(Boolean));

    if (editingId) {
      const nextEditing = newOrder.indexOf(editingId);
      setEditingIndex(nextEditing >= 0 ? nextEditing : null);
    }
    if (deleteId) {
      const nextDelete = newOrder.indexOf(deleteId);
      setDeleteIndex(nextDelete >= 0 ? nextDelete : null);
    }
    if (defaultSavePreset && defaultId) {
      const nextDefault = newOrder.indexOf(defaultId);
      if (nextDefault >= 0) {
        setDefaultSavePreset({ ...defaultSavePreset, index: nextDefault });
      }
    }
  }

  function addPresetTicket(preset: TicketPreset) {
    closeEditingTicket();
    const ticket = ticketFromPreset(communityId, preset);
    prepend({
      ...ticket,
      name: uniqueFallbackName(ticket.name),
    });
    setEditingIndex(0);
  }

  function addCustomTicket() {
    closeEditingTicket();
    prepend({
      name: '',
      type: 'OPEN_FOR_ALL',
      price: 0,
      quantity: null,
      enabled: true,
      allowedAttendeeTypes: ['GUEST'],
      formSchema: [],
    });
    setEditingIndex(0);
  }

  function finishEditing(index: number) {
    const ticket = form.getValues(`tickets.${index}`);
    const name = ticket?.name?.trim();
    if (!name) {
      form.setValue(
        `tickets.${index}.name`,
        uniqueFallbackName(ticketTitle(ticket, index), index),
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    } else if (isDuplicateName(name, index)) {
      form.setError(`tickets.${index}.name`, { message: 'Ticket names must be unique' });
      return;
    }
    form.clearErrors(`tickets.${index}.name`);
    setEditingIndex(null);
  }

  function confirmDeleteTicket() {
    if (deleteIndex == null) return;
    const index = deleteIndex;
    remove(index);
    setDeleteIndex(null);
    if (editingIndex == null) return;
    if (editingIndex === index) setEditingIndex(null);
    else if (editingIndex > index) setEditingIndex(editingIndex - 1);
  }

  function confirmSaveDefault() {
    if (!defaultSavePreset) return;
    const ticket = form.getValues(`tickets.${defaultSavePreset.index}`);
    saveTicketDefault(communityId, defaultSavePreset.preset, {
      name: ticket.name,
      type: ticket.type,
      price: ticket.price,
      quantity: ticket.quantity,
      allowedAttendeeTypes: ticket.allowedAttendeeTypes,
    });
    toast.success(
      `${PRESET_LABELS[defaultSavePreset.preset]} ticket default saved for future events`
    );
    setDefaultSavePreset(null);
    setEditingIndex(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Tickets</h3>
          <p className="text-sm text-muted-foreground">
            New tickets appear at the top. Drag the handle to rearrange order.
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm">
              <Plus className="mr-2 h-4 w-4" /> Add Ticket
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => addPresetTicket('member')}>
              Member ticket
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => addPresetTicket('guest')}>
              Guest / Visitor ticket
            </DropdownMenuItem>
            <DropdownMenuItem onClick={addCustomTicket}>Custom ticket</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Reorder.Group
        axis="y"
        values={fieldIds}
        onReorder={handleReorder}
        className="space-y-3"
        as="div"
      >
        {fields.map((field, index) => {
          const ticket = tickets[index] ?? (field as unknown as TicketValues);
          const preset = ticket.preset;
          const isEditing = editingIndex === index;

          if (!isEditing) {
            return (
              <TicketLabel
                key={field.id}
                form={form}
                ticket={ticket}
                index={index}
                fieldId={field.id}
                enabled={ticket.enabled !== false}
                onEnabledChange={(next) =>
                  form.setValue(`tickets.${index}.enabled`, next, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                onEdit={() => beginEditing(index)}
                onRemove={() => setDeleteIndex(index)}
              />
            );
          }

          const title = ticketTitle(ticket, index);
          const enabled = ticket.enabled !== false;

          return (
            <SortableTicketItem key={field.id} id={field.id}>
              {(controls) => (
            <div className="flex items-start gap-2">
              <div className="flex shrink-0 flex-col items-center gap-1 pt-3">
                <DragHandle controls={controls} label={title} />
                <Checkbox
                  checked={enabled}
                  onCheckedChange={(checked) =>
                    form.setValue(`tickets.${index}.enabled`, checked === true, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  aria-label={enabled ? `Disable ${title}` : `Enable ${title}`}
                  className={cn(!enabled && 'border-amber-700/70 bg-white dark:border-amber-200/80')}
                />
              </div>
              <Card
                className={cn(
                  'relative min-w-0 flex-1',
                  !enabled &&
                    'border-dashed border-amber-500/50 bg-amber-50 shadow-sm dark:border-amber-400/40 dark:bg-amber-950/40'
                )}
              >
                <CardHeader className="pr-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-base">
                      {preset ? `Edit ${PRESET_LABELS[preset]} Ticket` : `Edit Ticket #${index + 1}`}
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-2">
                      {preset ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setDefaultSavePreset({ index, preset })}
                        >
                          <Save className="mr-2 h-3.5 w-3.5" />
                          Save as default
                        </Button>
                      ) : null}
                      <Button type="button" size="sm" onClick={() => finishEditing(index)}>
                        Done
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => setDeleteIndex(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {preset ? (
                    <p className="text-xs text-muted-foreground">
                      Edits apply to this event only unless you save them as the default{' '}
                      {PRESET_LABELS[preset].toLowerCase()} ticket.
                    </p>
                  ) : null}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name={`tickets.${index}.name`}
                      render={({ field: nameField }) => (
                        <FormItem>
                          <FormLabel>Ticket Name</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Early Bird" {...nameField} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`tickets.${index}.type`}
                      render={({ field: typeField }) => (
                        <FormItem>
                          <FormLabel>Who can buy?</FormLabel>
                          <Select onValueChange={typeField.onChange} value={typeField.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="MEMBER_ONLY">Members Only</SelectItem>
                              <SelectItem value="MEMBER_GUEST">Member + Guest</SelectItem>
                              <SelectItem value="OPEN_FOR_ALL">Open for All</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`tickets.${index}.price`}
                    render={({ field: priceField }) => (
                      <FormItem>
                        <FormLabel>Price</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            {...priceField}
                            onChange={(e) => {
                              const next = parseNonNegativeInt(e.target.value);
                              priceField.onChange(next ?? 0);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`tickets.${index}.quantity`}
                    render={({ field: quantityField }) => (
                      <FormItem>
                        <div className="flex items-center gap-1.5">
                          <FormLabel className="mb-0">Quantity</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
                                aria-label="What is quantity?"
                              >
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="text-sm">
                              Quantity is the maximum number of this ticket that can be registered.
                              Leave it empty or reset it for unlimited capacity.
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              inputMode="numeric"
                              placeholder="Unlimited"
                              className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                              value={quantityField.value ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (!raw.trim()) {
                                  quantityField.onChange(null);
                                  return;
                                }
                                const parsed = Number.parseInt(raw, 10);
                                if (!Number.isFinite(parsed) || parsed < 0) return;
                                quantityField.onChange(parsed);
                              }}
                              onKeyDown={(e) => {
                                if (
                                  e.key === '-' ||
                                  e.key === 'e' ||
                                  e.key === 'E' ||
                                  e.key === '+'
                                ) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            title="Reset to unlimited"
                            aria-label="Reset quantity to unlimited"
                            disabled={quantityField.value == null}
                            onClick={() => quantityField.onChange(null)}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name={`tickets.${index}.allowedAttendeeTypes`}
                  render={({ field: attendeeField }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Who is this ticket for?</FormLabel>
                      <FormControl>
                        <div className="flex flex-wrap gap-4 rounded-md border bg-muted/20 p-3">
                          {['SELF', 'MEMBER', 'GUEST'].map((type) => (
                            <label
                              key={type}
                              className="group flex cursor-pointer items-center space-x-2"
                            >
                              <input
                                type="checkbox"
                                checked={attendeeField.value?.includes(type)}
                                onChange={(e) => {
                                  const currentValues = attendeeField.value || [];
                                  const nextValues = e.target.checked
                                    ? [...currentValues, type]
                                    : currentValues.filter((v: string) => v !== type);
                                  attendeeField.onChange(nextValues);
                                }}
                                className="h-4 w-4 rounded border-primary text-primary focus:ring-primary"
                              />
                              <span className="text-sm font-medium capitalize">
                                {type.toLowerCase()}
                              </span>
                            </label>
                          ))}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="border-t pt-4">
                  <h4 className="mb-3 text-sm font-medium">Additional Details</h4>
                  <CustomFormBuilder form={form} nestIndex={index} />
                </div>
              </CardContent>
            </Card>
            </div>
              )}
            </SortableTicketItem>
          );
        })}
      </Reorder.Group>

      <AlertDialog
        open={deleteIndex != null}
        onOpenChange={(open) => {
          if (!open) setDeleteIndex(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              {ticketPendingDelete
                ? `"${ticketTitle(ticketPendingDelete, deleteIndex ?? 0)}" will be removed from this event.`
                : 'This ticket will be removed from this event.'}{' '}
              You can add it again later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteTicket}
            >
              Delete ticket
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={defaultSavePreset != null}
        onOpenChange={(open) => {
          if (!open) setDefaultSavePreset(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Save {defaultSavePreset ? PRESET_LABELS[defaultSavePreset.preset] : ''} ticket
              default?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This updates the preconfigured{' '}
              {defaultSavePreset
                ? PRESET_LABELS[defaultSavePreset.preset].toLowerCase()
                : ''}{' '}
              ticket used whenever that option is selected for future events in this community. The
              current event already uses the values you edited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep for this event only</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSaveDefault}>Save as default</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
